// campaignsList.js — campaign library
//
// Uses ActiveHelper/MainPage for consistency with the segments + vouchers + admins lists.
// Status filter shown as a "Status" column (Draft / Scheduled / Running / Completed / Canceled).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import ActiveHelper from '../../../../components/common/activeHelper';
import { firestore } from '../../../../helpers/Firebase';
import { AuditLogModal, useAuditLogModal, actorFromUser, writeAuditEntry } from '../shared/auditLog.js';

const STATUS_LABELS = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  canceled: 'Canceled',
};

const CampaignsList = ({ history }) => {
  const { user } = useSelector(state => state.authUser);
  const search = useSelector(state => state.settings.search) || '';

  const [campaigns, setCampaigns] = useState([]);
  const [filtered, setFiltered] = useState([]);

  useEffect(() => {
    const unsub = firestore()
      .collection('marketing_campaigns')
      .orderBy('updatedAt', 'desc')
      .onSnapshot(
        (snap) => setCampaigns(snap.docs.map(d => ({ id: d.id, key: d.id, ...d.data() }))),
        (err) => console.error('campaigns listener error', err)
      );
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!search || !search.trim()) { setFiltered(campaigns); return; }
    const q = search.toLowerCase().trim();
    setFiltered(campaigns.filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.description || '').toLowerCase().includes(q) ||
      (STATUS_LABELS[c.status] || '').toLowerCase().includes(q)
    ));
  }, [campaigns, search]);

  const decorated = useMemo(() => filtered.map(c => ({
    ...c,
    _status: STATUS_LABELS[c.status] || c.status || 'Draft',
    _segment: c.segmentName || (c.segmentId ? 'ad-hoc' : '—'),
    _sendAt: c.schedule?.sendAt || null,
    _sent: c.stats?.sent || 0,
    _audience: c.audience?.resolved || c.audience?.estimated || 0,
    _updatedAt: c.updatedAt || c.createdAt,
  })), [filtered]);

  const tableColumns = useMemo(() => [
    { Header: 'Name', accessor: 'name', type: 'label' },
    { Header: 'Status', accessor: '_status', type: 'label' },
    { Header: 'Segment', accessor: '_segment', type: 'label' },
    {
      Header: 'Send at',
      accessor: '_sendAt',
      type: 'label',
      formatValue: (v) => v ? moment(v).format('DD/MM/YYYY HH:mm') : '—',
    },
    { Header: 'Audience', accessor: '_audience', type: 'label' },
    { Header: 'Sent', accessor: '_sent', type: 'label' },
    {
      Header: 'Last updated',
      accessor: '_updatedAt',
      type: 'label',
      formatValue: (v) => v ? moment(v).fromNow() : '—',
    },
  ], []);

  const handleEdit = useCallback((c) => {
    if (!c) return;
    history.push(`/app/marketing/campaigns/${c.id || c.key}/edit`);
  }, [history]);

  const handleCreate = useCallback(() => history.push('/app/marketing/campaigns/new'), [history]);

  const handleDelete = useCallback(async (c) => {
    if (!c) return;
    // Only allow delete on draft / canceled / failed — never on running/completed
    if (c.status && !['draft', 'canceled', 'failed'].includes(c.status)) {
      alert(`Cannot delete a campaign in status "${c.status}". Cancel it first.`);
      return;
    }
    if (!window.confirm(`Delete campaign "${c.name || c.id}"? This cannot be undone.`)) return;
    const id = c.id || c.key;
    try {
      writeAuditEntry('marketing_campaigns', id, {
        ...actorFromUser(user),
        action: 'deleted',
        notes: `Deleted campaign "${c.name || id}" (status: ${c.status || 'unknown'})`,
      });
      await firestore().collection('marketing_campaigns').doc(id).delete();
    } catch (e) {
      console.error('campaign delete failed', e);
    }
  }, [user]);

  const exportCSV = useCallback((data) => {
    if (!data || !data.length) return;
    const headers = ['Name', 'Status', 'Segment', 'Send at', 'Audience', 'Sent', 'Last updated'];
    const rows = data.map(c => [
      `"${(c.name || '').replace(/"/g, '""')}"`,
      c._status,
      c._segment,
      c._sendAt ? moment(c._sendAt).format('YYYY-MM-DD HH:mm') : '',
      c._audience,
      c._sent,
      c._updatedAt ? moment(c._updatedAt).format('YYYY-MM-DD HH:mm') : '',
    ].join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `campaigns_${moment().format('YYYY-MM-DD')}.csv`;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  }, []);

  const logsModal = useAuditLogModal();

  return (
    <>
    <ActiveHelper
      title="Marketing Campaigns"
      selected="campaigns"
      data={decorated}
      tableColumns={tableColumns}
      customActionButtons={[
        {
          accessor: 'analytics',
          title: 'Analytics',
          color: 'primary',
          func: (row) => history.push(`/app/marketing/campaigns/${row.id || row.key}/analytics`),
        },
        {
          accessor: 'logs',
          title: 'Logs',
          color: 'info',
          func: (row) => logsModal.open(row),
        },
        // Custom Delete replaces the default one so we can hide it per-row.
        // Only draft / canceled / failed campaigns can be removed —
        // running/completed campaigns must stay for the historical record.
        {
          accessor: 'delete',
          title: 'Delete',
          color: 'danger',
          show: (row) => user?.accessType === 'superAdmin'
            && ['draft', 'canceled', 'failed'].includes(row.status || 'draft'),
          func: (row) => handleDelete(row),
        },
      ]}
      hideDefaultDeleteButton={true}
      permenatDelete={true}
      defaultSorting="_updatedAt"
      sortDirection="desc"
      exportCSV={exportCSV}
      customCreateFunction={handleCreate}
      customEditFunction={handleEdit}
      customDeleteFunction={handleDelete}
      confirmCustomDelete={true}
      showCreateButton={true}
      user={user}
    />
    <AuditLogModal
      isOpen={logsModal.isOpen}
      dismiss={logsModal.close}
      collection="marketing_campaigns"
      docId={logsModal.row?.id || logsModal.row?.key}
      title={logsModal.row?.name}
    />
    </>
  );
};

export default CampaignsList;
